import { useEffect, useRef, useState } from 'react'
import { track } from '@/lib/analytics'
import { useLabMembership } from './useLabMembership'

/**
 * v3.1a §2.0 Lab-membership blocking overlay.
 *
 * Условия рендера:
 *  - `usePollsAccess()===true` (гейт whitelist пройден)
 *  - `useLabMembership()` вернул `isMember=false` (или fail-open спас нас,
 *    но тогда компонент вообще не рендерится — fail-open → `isMember=true`)
 *
 * Триггеры refetch (§2.0):
 *  1. `document.visibilitychange` → visible → recheck() с `X-Lab-Recheck: true`.
 *  2. Клик по CTA «Подписаться и продолжить» → откроем deep-link в новой вкладке
 *     + запустим 30s-polling fallback (R2, для MAX webview lifecycle).
 *  3. Manual «Уже подписался, проверить» кнопка в overlay.
 *
 * Fallback polling (R2): после клика CTA — тикающий interval `recheck()` каждые
 * 30 секунд. Три exit-условия: (a) isMember=true (закроем overlay), (b) юзер
 * закрыл вкладку/страницу (React cleanup), (c) 5 минут прошло.
 */
type CheckStatus = 'idle' | 'checking' | 'stillNotSubscribed'

export function LabMembershipOverlay({ children }: { children: React.ReactNode }) {
  const { isLoading, isMember, labChannelDeepLink, recheck } = useLabMembership()

  const pollingRef = useRef<{ interval: number | null; started: number } | null>(null)
  const [checkStatus, setCheckStatus] = useState<CheckStatus>('idle')

  // Триггер 1: visibilitychange
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void recheck()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [recheck])

  // Триггер 2: overlay появился (isMember=false) — эмитим гейт-событие
  useEffect(() => {
    if (isLoading) return
    if (!isMember) {
      track('poll_gate_shown', { route: window.location.pathname })
    } else if (pollingRef.current) {
      // Юзер только что подписался — эмитим passed + чистим polling
      track('poll_gate_passed', { previouslyBlocked: true })
      clearInterval(pollingRef.current.interval!)
      pollingRef.current = null
    }
  }, [isMember, isLoading])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current?.interval) {
        clearInterval(pollingRef.current.interval)
        pollingRef.current = null
      }
    }
  }, [])

  const startFallbackPolling = () => {
    // Уже запущен — не дублируем
    if (pollingRef.current) return
    const started = Date.now()
    const interval = window.setInterval(() => {
      // Условие (c): 5 мин прошло — стоп
      if (Date.now() - started > 5 * 60 * 1000) {
        clearInterval(interval)
        pollingRef.current = null
        return
      }
      // Условие (a): isMember=true — сработает через useEffect выше
      void recheck()
    }, 30_000)
    pollingRef.current = { interval, started }
  }

  const handleSubscribeClick = () => {
    track('poll_gate_cta_clicked', { route: window.location.pathname, hasDeepLink: !!labChannelDeepLink })
    if (labChannelDeepLink) window.open(labChannelDeepLink, '_blank', 'noopener,noreferrer')
    startFallbackPolling()
  }

 const handleRecheckClick = async () => {
    if (checkStatus === 'checking') return
    setCheckStatus('checking')
    const fresh = await recheck() // recheck() сам эмитит poll_gate_refresh_clicked
    // Если isMember=true — overlay сам исчезнет через useEffect на [isMember].
    // Если !isMember — показываем inline note что мы проверили.
    if (fresh?.isMember === false) {
      setCheckStatus('stillNotSubscribed')
      // Auto-reset через 5 секунд — юзер увидит подтверждение, потом кнопка снова активна.
      window.setTimeout(() => setCheckStatus('idle'), 5000)
    } else {
      // isMember=true (или fail-open null → true) — overlay уходит, но
      // на случай race сбросим checkStatus.
      setCheckStatus('idle')
    }
  }

  return (
    <>
      {children}
      {!isLoading && !isMember && (
        <div className="lab-gate-veil" role="dialog" aria-modal="true">
          <div className="lab-gate">
            <div className="lab-gate-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h4l3 8 4-16 3 8h4" />
              </svg>
            </div>
            <h2 className="lab-gate-h">Опросы работают, пока вы с нами</h2>
            <p className="lab-gate-p">
              Мы отдаём опросы для MAX-каналов <b>бесплатно</b>. Взамен просим о малом —
              подпишитесь на наш канал <b>Telegraphyx Lab</b>. Отпишетесь — доступ к аналитике опросов закроется.
            </p>
            <div className="lab-gate-status">
              <span className="lab-gate-dot" />
              Вы <b>не подписаны</b> на Telegraphyx Lab
            </div>
            <button className="lab-gate-btn" type="button" onClick={handleSubscribeClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Подписаться и продолжить
            </button>
            <button
              className="lab-gate-recheck"
              type="button"
              onClick={handleRecheckClick}
              disabled={checkStatus === 'checking'}
              aria-busy={checkStatus === 'checking'}
            >
              {checkStatus === 'checking' ? (
                <>
                  <span className="lab-gate-spinner" aria-hidden="true" /> Проверяем…
                </>
              ) : (
                'Уже подписался, проверить'
              )}
            </button>
            {checkStatus === 'stillNotSubscribed' && (
              <p className="lab-gate-still-not" role="status">
                Мы проверили — вы ещё не подписаны на Telegraphyx&nbsp;Lab.
              </p>
            )}
            <p className="lab-gate-note">
              После подписки опросы включатся автоматически — обычно занимает пару секунд.
            </p>
          </div>
          <OverlayStyles />
        </div>
      )}
    </>
  )
}

function OverlayStyles() {
  return (
    <style>{`
      .lab-gate-veil {
        position: fixed; inset: 0; z-index: 200;
        background: rgba(15,19,28,0.55);
        backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        padding: 24px;
      }
      .lab-gate {
        background: var(--tg-surface, #fff);
        border-radius: 22px;
        max-width: 440px; width: 100%;
        padding: 36px 34px 30px;
        text-align: center;
        box-shadow: 0 30px 80px rgba(0,0,0,0.3);
      }
      .lab-gate-icon {
        width: 60px; height: 60px; border-radius: 18px;
        margin: 0 auto 20px;
        background: linear-gradient(135deg, var(--tg-blue, #0066ff), var(--tg-blue-press, #0052cc));
        color: #fff;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 10px 26px -8px rgba(0,102,255,0.6);
      }
      .lab-gate-icon svg { width: 30px; height: 30px; }
      .lab-gate-h {
        font-weight: 800; font-size: 23px;
        letter-spacing: -0.02em;
        color: var(--tg-ink, #111);
        margin: 0 0 10px;
      }
      .lab-gate-p {
        font-size: 14.5px; line-height: 1.55;
        color: var(--tg-ink-2, #333);
        margin: 0 0 20px;
      }
      .lab-gate-p b { color: var(--tg-ink, #111); font-weight: 700; }
      .lab-gate-status {
        display: inline-flex; align-items: center; gap: 8px;
        background: rgba(232,53,53,0.1);
        color: #dc3545;
        font-size: 13px; font-weight: 600;
        padding: 8px 14px;
        border-radius: 999px;
        margin-bottom: 20px;
      }
      .lab-gate-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: #dc3545;
      }
      .lab-gate-btn {
        display: flex; width: 100%;
        align-items: center; justify-content: center; gap: 9px;
        background: var(--tg-blue, #0066ff);
        color: #fff; border: 0;
        font-family: inherit; font-weight: 700; font-size: 15px;
        padding: 14px; border-radius: 14px;
        cursor: pointer;
        box-shadow: 0 8px 22px -8px rgba(0,102,255,0.6);
        transition: background 150ms;
      }
      .lab-gate-btn:hover { background: var(--tg-blue-hover, #0052cc); }
      .lab-gate-btn svg { width: 18px; height: 18px; }
      .lab-gate-recheck {
        display: inline-flex; align-items: center; justify-content: center;
        gap: 8px; width: 100%;
        margin-top: 10px;
        background: transparent; border: 0;
        color: var(--tg-mute, #8f9eb0);
        font-family: inherit; font-size: 13px; font-weight: 500;
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
        transition: color 150ms;
      }
      .lab-gate-recheck:hover:not(:disabled) { color: var(--tg-ink-2, #333); }
      .lab-gate-recheck:disabled { cursor: default; text-decoration: none; }
      .lab-gate-spinner {
        width: 12px; height: 12px; border-radius: 50%;
        border: 2px solid rgba(143,158,176,0.3);
        border-top-color: var(--tg-mute, #8f9eb0);
        animation: lab-gate-spin 0.7s linear infinite;
      }
      @keyframes lab-gate-spin { to { transform: rotate(360deg); } }
      .lab-gate-still-not {
        margin: 12px 0 0;
        padding: 10px 14px;
        background: rgba(232,53,53,0.08);
        border: 1px solid rgba(232,53,53,0.20);
        border-radius: 10px;
        font-size: 12.5px; line-height: 1.45;
        color: #a03030;
      }
      .lab-gate-note {
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0);
        margin: 14px 0 0;
      }
    `}</style>
  )
}
